import { jwtDecode } from "jwt-decode";
import { ApiError, isApiError } from "@/shared/api/errors";
import { triggerDeviceRegistration } from "@/shared/lib/registerDevice";
import { clearAllTokens, getRefreshToken, getToken, setToken } from "./tokenStorage";
import { emitAuthSessionInvalid, type AuthSessionInvalidCode } from "./authSessionEvents";

/** access token 만료 임박 판정 여유. exp - now <= 60초이면 사전 갱신한다. */
const REFRESH_SKEW_SECONDS = 60;

/**
 * refresh 실패 errorCode 중 세션을 무효화하고 토큰을 폐기해야 하는 코드.
 * 그 외(INVALID_REQUEST, 서버 오류, 네트워크 오류, timeout)는 토큰을 유지한다.
 */
const SESSION_INVALID_CODES = new Set<AuthSessionInvalidCode>([
  "REFRESH_TOKEN_EXPIRED",
  "REFRESH_TOKEN_INVALID",
  "REFRESH_TOKEN_REUSED",
  "USER_WITHDRAWN",
  "USER_SUSPENDED",
  "USER_BLOCKED",
]);

type JwtPayload = {
  exp?: number;
};

export type RefreshTokenResponse = {
  grantType: "Bearer";
  accessToken: string;
  refreshToken: string;
};

export type RefreshCoordinatorOptions = {
  requestRefresh: (refreshToken: string) => Promise<RefreshTokenResponse>;
};

const isAccessTokenExpiringSoon = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    if (!exp) return true;
    return exp <= Date.now() / 1000 + REFRESH_SKEW_SECONDS;
  } catch {
    return true;
  }
};

export const createRefreshCoordinator = ({ requestRefresh }: RefreshCoordinatorOptions) => {
  // 진행 중인 refresh Promise. 동시 호출은 같은 Promise 를 공유해 1회만 송신한다.
  // 백엔드 refresh token 은 one-shot rotation 이라 동시 송신 시 REFRESH_TOKEN_REUSED 가 발동한다.
  let refreshPromise: Promise<string> | null = null;

  const performRefresh = async (hadAuthToken: boolean): Promise<string> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      // refresh 수단 자체가 없음(토큰 전무·리뷰 로그인). 이 분기는 서버 401 재시도 경로에서만 도달한다.
      if (!hadAuthToken) {
        // 토큰 없이 나간 요청 — 무효화할 세션이 없다. 토큰 폐기·세션 무효 이벤트 없이 실패만 알린다(#405).
        // (이벤트를 내면 teardown 뒤 exit 중 옵저버의 재요청이 logOut 을 재진입시킨다.) 인증 client 는
        // 토큰 없는 요청을 clients.ts 에서 로컬 거절하므로 정상 경로에선 닿지 않는 방어선이다.
        throw new ApiError("로그인이 필요합니다.", {
          kind: "http",
          status: 401,
          errorCode: "AUTH_TOKEN_MISSING",
        });
      }
      // bearer 를 실었는데 401 = "서버가 방금 거절한 세션" = 만료. 재로그인만 가능하므로 세션 무효로 승격한다.
      const errorCode: AuthSessionInvalidCode = "SESSION_EXPIRED";
      clearAllTokens();
      emitAuthSessionInvalid({ errorCode });
      throw new ApiError("로그인이 만료되었습니다.", { kind: "http", status: 401, errorCode });
    }

    try {
      const data = await requestRefresh(refreshToken);
      // 응답 즉시 이전 토큰을 새 쌍으로 교체.
      setToken(data.accessToken, data.refreshToken);
      // refresh 로 access token 이 바뀌었으므로 디바이스 재등록 트리거 (DEVICE_REGISTRATION §5.1). best-effort.
      triggerDeviceRegistration(data.accessToken);
      return data.accessToken;
    } catch (error) {
      if (isApiError(error)) {
        const { errorCode, detail } = error;
        if (errorCode && SESSION_INVALID_CODES.has(errorCode as AuthSessionInvalidCode)) {
          clearAllTokens();
          emitAuthSessionInvalid({ errorCode: errorCode as AuthSessionInvalidCode, detail });
        }
      }
      // 그 외 오류는 토큰을 유지한 채 그대로 전파한다.
      throw error;
    }
  };

  // hadAuthToken 은 필수 인자(기본값 없음 — 미래 직접 호출자가 부재 분기 분류를 흐리지 않도록).
  const refreshOnce = (hadAuthToken: boolean): Promise<string> => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = performRefresh(hadAuthToken).finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  };

  /**
   * 보호 요청 전 호출. refresh token 이 있고 access token 이 없거나 만료 임박이면
   * 사전 갱신하고, 그 외에는 현재 access token 을 그대로 반환한다.
   */
  const ensureValidAccessToken = async (): Promise<string | null> => {
    const accessToken = getToken();
    const refreshToken = getRefreshToken();
    if (!refreshToken) return accessToken;
    if (!accessToken || isAccessTokenExpiringSoon(accessToken)) {
      // refresh token 존재가 전제라 부재 분기에 닿지 않으며, 갱신 대상은 인증 요청이므로 true.
      return refreshOnce(true);
    }
    return accessToken;
  };

  return {
    ensureValidAccessToken,
    refreshOnce,
  };
};
